import { useCallback, useEffect, useState } from 'react';
import { formatNumber } from '@/utils/global';
import { CURRENCY_usd, getCurrencySymbol } from '@/utils/ts/global';
import { useSelector } from 'react-redux';

interface CurrencyApiResponse {
  date: string;
  usd: Record<string, number>;
}

interface ConversionResult {
  amount: number;
  formatted: string;
}

interface CurrencyRates {
  [key: string]: number;
}

const useCurrencyConverter = () => {
  const [rates, setRates] = useState<CurrencyRates>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const { userDetails } = useSelector((state: any) => state.user);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);

    const primaryUrl = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json';
    const fallbackUrl = 'https://latest.currency-api.pages.dev/v1/currencies/usd.json';

    try {
      // Try primary API first
      let response = await fetch(primaryUrl);

      if (!response.ok) {
        console.warn(`Primary API failed with status: ${response.status}, trying fallback...`);
        // Try fallback API
        response = await fetch(fallbackUrl);

        if (!response.ok) {
          throw new Error(`Both APIs failed. Fallback status: ${response.status}`);
        }
      }

      const data: CurrencyApiResponse = await response.json();
      setRates(data.usd);
      setLastUpdated(data.date);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch currency rates from all sources');
      console.error('Error fetching currency rates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const getSymbol = useCallback((currency: string): string => {
    return getCurrencySymbol(currency.toUpperCase());
  }, []);

  const formatAmount = useCallback(
    (amount: number, currency: string): string => {
      const symbol = getSymbol(currency);
      const formattedAmount = formatNumber(amount, 2);
      return `${symbol}${formattedAmount}`;
    },
    [getSymbol]
  );

  const convertCurrency = useCallback(
    (amount: number, fromCurrency: string, toCurrency: string): ConversionResult => {
      if (!rates || Object.keys(rates).length === 0) {
        return {
          amount: 0,
          formatted: formatAmount(0, toCurrency),
        };
      }

      const fromKey = fromCurrency.toLowerCase();
      const toKey = toCurrency.toLowerCase();

      // If converting from USD
      if (fromKey === CURRENCY_usd) {
        const toRate = rates[toKey];
        if (!toRate) {
          console.warn(`Currency ${toCurrency} not found in rates`);
          return {
            amount: 0,
            formatted: formatAmount(0, toCurrency),
          };
        }
        const convertedAmount = amount * toRate;
        return {
          amount: convertedAmount,
          formatted: formatAmount(convertedAmount, toCurrency),
        };
      }

      // If converting to USD
      if (toKey === CURRENCY_usd) {
        const fromRate = rates[fromKey];
        if (!fromRate) {
          console.warn(`Currency ${fromCurrency} not found in rates`);
          return {
            amount: 0,
            formatted: formatAmount(0, toCurrency),
          };
        }
        const convertedAmount = amount / fromRate;
        return {
          amount: convertedAmount,
          formatted: formatAmount(convertedAmount, toCurrency),
        };
      }

      // Converting between two non-USD currencies
      const fromRate = rates[fromKey];
      const toRate = rates[toKey];

      if (!fromRate || !toRate) {
        console.warn(`Currency ${fromRate ? toCurrency : fromCurrency} not found in rates`);
        return {
          amount: 0,
          formatted: formatAmount(0, toCurrency),
        };
      }

      // Convert from source currency to USD, then USD to target currency
      const usdAmount = amount / fromRate;
      const convertedAmount = usdAmount * toRate;

      return {
        amount: convertedAmount,
        formatted: formatAmount(convertedAmount, toCurrency),
      };
    },
    [rates, formatAmount]
  );

  const convertToUserCurrency = useCallback(
    (amount: number, fromCurrency: string = CURRENCY_usd): ConversionResult => {
      const userCurrency = userDetails?.defaultCurrency?.toLowerCase() || CURRENCY_usd;
      return convertCurrency(amount, fromCurrency, userCurrency);
    },
    [convertCurrency, userDetails?.defaultCurrency]
  );

  return {
    rates,
    loading,
    error,
    lastUpdated,
    convertCurrency,
    convertToUserCurrency,
    getCurrencySymbol: getSymbol,
    formatAmount,
    refetch: fetchRates,
  };
};

export default useCurrencyConverter;
